import { useState, useCallback, useMemo } from 'react';
import { FaChevronDown, FaChevronRight, FaCheckSquare, FaRegSquare, FaSearch, FaFileInvoiceDollar, FaMagic } from 'react-icons/fa';
import { Auth } from 'aws-amplify';
import BadgeStatus from '../../atoms/BadgeStatus';
import appConfig from '../../../config/app-config';
import { contractsManagementApi, OperationResult } from '../../../services/contractsManagementApi';
import ReceivingBankAccountSelect from './components/ReceivingBankAccountSelect';

const { address: SETTLEMENT_URL, key: SETTLEMENT_KEY } = appConfig.apis.settlementV2;

// ─── Types ──────────────────────────────────────────────────────────────────

interface TitleDebt {
    id: number;
    contract_number: string;
    credit_operation_id: string;
    installment_number: number;
    amount: number;
    paid_amount: number;
    due_date: string;
    status: string;
    document_title: string | null;
    business_reference: string;
    debtor?: { name: string; document: string | null };
    payer?: { name: string; document: string | null };
}

interface ContractGroup {
    operation_number: string;
    credit_operation_id: string;
    operation_type: string;
    approved_value: number;
    participants: any[];
    debts: TitleDebt[];
    total_open: number;
    expanded: boolean;
    selected: boolean;
    debtsLoaded: boolean;
}

interface PaymentInfo {
    amount: string;
    date: string;
    payer_name: string;
    payer_document: string;
    bank_account: string;
    payment_method: string;
    notes: string;
}

// ─── Steps ──────────────────────────────────────────────────────────────────

const STEPS = ['Dados do Recebimento', 'Selecionar Contratos', 'Selecionar Títulos'] as const;

// ─── Helpers ────────────────────────────────────────────────────────────────

const fmt = (n: number) => n.toLocaleString('pt-BR', { minimumFractionDigits: 2 });

const fetchTitleDebts = async (creditOpId: string): Promise<TitleDebt[]> => {
    const session = await Auth.currentSession();
    const token = session.getIdToken().getJwtToken();
    const params = new URLSearchParams({ page: '1', size: '500', credit_operation_id: creditOpId, 'filter[status]': 'OPEN' });
    const res = await fetch(`${SETTLEMENT_URL}/title-debts?${params}`, {
        headers: { 'x-api-key': SETTLEMENT_KEY, 'Authorization': `Bearer ${token}` },
    });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const data = await res.json();
    return data.list || [];
};

// ─── Main Component ─────────────────────────────────────────────────────────

const RegisterPaymentPage = () => {
    const [currentStep, setCurrentStep] = useState(0);
    const [payment, setPayment] = useState<PaymentInfo>({
        amount: '', date: new Date().toISOString().split('T')[0],
        payer_name: '', payer_document: '', bank_account: '',
        payment_method: 'FINANCIAL', notes: '',
    });

    // Step 2: Contracts
    const [searchResults, setSearchResults] = useState<ContractGroup[]>([]);
    const [selectedContracts, setSelectedContracts] = useState<ContractGroup[]>([]);
    const [loading, setLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [searchExecuted, setSearchExecuted] = useState(false);

    // Step 3: Title debts
    const [selectedDebts, setSelectedDebts] = useState<TitleDebt[]>([]);

    const paymentAmount = parseFloat(payment.amount.replace(/\./g, '').replace(',', '.')) || 0;

    // ─── Step 1: Payment info ────────────────────────────────────────────────
    const updatePayment = (field: keyof PaymentInfo, value: string) => {
        setPayment(prev => ({ ...prev, [field]: value }));
    };

    // ─── Step 2: Contract search ─────────────────────────────────────────────
    const searchContracts = useCallback(async () => {
        if (!searchTerm || searchTerm.length < 2) return;
        setLoading(true);
        setSearchExecuted(true);
        try {
            const results: OperationResult[] = await contractsManagementApi.searchOperations(searchTerm);
            setSearchResults(results.map(op => ({
                operation_number: op.operation_number || `#${op.sequencial_number}`,
                credit_operation_id: op.credit_operation_id,
                operation_type: op.operation_type,
                approved_value: op.approved_value || op.loan_amount || 0,
                participants: op.participants || [],
                debts: [], total_open: 0, expanded: false,
                selected: !!selectedContracts.find(sc => sc.credit_operation_id === op.credit_operation_id),
                debtsLoaded: false,
            })));
        } catch (e) { console.error(e); }
        finally { setLoading(false); }
    }, [searchTerm, selectedContracts]);

    const toggleContractExpand = async (idx: number) => {
        const contract = searchResults[idx];
        if (!contract.debtsLoaded) {
            try {
                const debts = await fetchTitleDebts(contract.credit_operation_id);
                const totalOpen = debts.reduce((s, d) => s + (Number(d.amount) - Number(d.paid_amount)), 0);
                setSearchResults(prev => prev.map((c, i) => i === idx ? { ...c, debts, total_open: totalOpen, expanded: true, debtsLoaded: true } : c));
                setSelectedContracts(prev => prev.map(c => c.credit_operation_id === contract.credit_operation_id ? { ...c, debts, total_open: totalOpen, debtsLoaded: true } : c));
            } catch (e) { console.error(e); }
        } else {
            setSearchResults(prev => prev.map((c, i) => i === idx ? { ...c, expanded: !c.expanded } : c));
        }
    };

    const toggleContractSelect = (idx: number) => {
        const contract = searchResults[idx];
        const exists = selectedContracts.find(c => c.credit_operation_id === contract.credit_operation_id);
        if (exists) {
            setSelectedContracts(prev => prev.filter(c => c.credit_operation_id !== contract.credit_operation_id));
        } else {
            setSelectedContracts(prev => [...prev, { ...contract, selected: true }]);
        }
        setSearchResults(prev => prev.map((c, i) => i === idx ? { ...c, selected: !c.selected } : c));
    };

    const removeSelectedContract = (id: string) => {
        setSelectedContracts(prev => prev.filter(c => c.credit_operation_id !== id));
        setSearchResults(prev => prev.map(c => c.credit_operation_id === id ? { ...c, selected: false } : c));
    };

    // ─── Step 3: Smart title selection ───────────────────────────────────────
    const goToStep3 = async () => {
        setLoading(true);
        try {
            const updated: ContractGroup[] = [];
            for (const c of selectedContracts) {
                if (c.debtsLoaded) { updated.push(c); }
                else {
                    const debts = await fetchTitleDebts(c.credit_operation_id);
                    const totalOpen = debts.reduce((s, d) => s + (Number(d.amount) - Number(d.paid_amount)), 0);
                    updated.push({ ...c, debts, total_open: totalOpen, debtsLoaded: true });
                }
            }
            setSelectedContracts(updated);
            // Auto-select debts up to payment amount (oldest first)
            const allDebts = updated.flatMap(c => c.debts).sort((a, b) => (a.due_date || '').localeCompare(b.due_date || ''));
            autoSelectDebts(allDebts);
            setCurrentStep(2);
        } finally { setLoading(false); }
    };

    const autoSelectDebts = (debts: TitleDebt[]) => {
        let remaining = paymentAmount;
        const selected: TitleDebt[] = [];
        for (const debt of debts) {
            const balance = Number(debt.amount) - Number(debt.paid_amount);
            if (remaining <= 0) break;
            selected.push(debt);
            remaining -= balance;
        }
        setSelectedDebts(selected);
    };

    const toggleDebtSelect = (debt: TitleDebt) => {
        setSelectedDebts(prev =>
            prev.find(d => d.id === debt.id) ? prev.filter(d => d.id !== debt.id) : [...prev, debt]
        );
    };

    const isDebtSelected = (debt: TitleDebt) => !!selectedDebts.find(d => d.id === debt.id);

    const totalSelected = useMemo(() =>
        selectedDebts.reduce((sum, d) => sum + (Number(d.amount) - Number(d.paid_amount)), 0), [selectedDebts]);

    const diff = totalSelected - paymentAmount;

    // ─── Render ──────────────────────────────────────────────────────────────
    return (
        <div className="p-6 max-w-7xl mx-auto">
            {/* Header */}
            <div className="mb-6">
                <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
                    <FaFileInvoiceDollar className="text-green-600" />
                    Registrar Recebimento
                </h1>
                <p className="text-sm text-gray-500 mt-1">Baixa em lote de títulos de dívida</p>
            </div>

            {/* Stepper */}
            <div className="flex items-center mb-8 bg-white rounded-lg p-4 shadow-sm">
                {STEPS.map((step, idx) => (
                    <div key={step} className="flex items-center flex-1">
                        <div className={`flex items-center gap-2 ${idx <= currentStep ? 'text-blue-600 font-semibold' : 'text-gray-400'}`}>
                            <span className={`w-7 h-7 rounded-full flex items-center justify-center text-sm ${idx < currentStep ? 'bg-green-500 text-white' : idx === currentStep ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-500'}`}>
                                {idx < currentStep ? '✓' : idx + 1}
                            </span>
                            <span className="text-sm">{step}</span>
                        </div>
                        {idx < STEPS.length - 1 && <div className={`flex-1 h-0.5 mx-4 ${idx < currentStep ? 'bg-green-500' : 'bg-gray-200'}`} />}
                    </div>
                ))}
            </div>

            {/* ═══ STEP 1: Payment Info ═══ */}
            {currentStep === 0 && (
                <div className="bg-white rounded-lg shadow-sm p-6">
                    <h2 className="font-semibold text-gray-800 mb-4">Informações do Recebimento</h2>
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="text-xs text-gray-500 block mb-1">Valor Recebido (R$) *</label>
                            <input type="text" value={payment.amount}
                                onChange={e => updatePayment('amount', e.target.value)}
                                placeholder="2.500.000,00"
                                className="w-full border rounded px-3 py-2 text-lg font-semibold" />
                        </div>
                        <div>
                            <label className="text-xs text-gray-500 block mb-1">Data do Pagamento *</label>
                            <input type="date" value={payment.date}
                                onChange={e => updatePayment('date', e.target.value)}
                                className="w-full border rounded px-3 py-2" />
                        </div>
                        <div>
                            <label className="text-xs text-gray-500 block mb-1">CPF/CNPJ de quem pagou *</label>
                            <input type="text" value={payment.payer_document}
                                onChange={e => updatePayment('payer_document', e.target.value)}
                                placeholder="00.000.000/0001-00"
                                className="w-full border rounded px-3 py-2" />
                        </div>
                        <div>
                            <label className="text-xs text-gray-500 block mb-1">Nome de quem pagou</label>
                            <input type="text" value={payment.payer_name}
                                onChange={e => updatePayment('payer_name', e.target.value)}
                                placeholder="Será buscado pelo CPF/CNPJ"
                                className="w-full border rounded px-3 py-2" />
                        </div>
                        <div>
                            <label className="text-xs text-gray-500 block mb-1">Instituição de Recebimento *</label>
                            <ReceivingBankAccountSelect
                                value={payment.bank_account}
                                onChange={v => updatePayment('bank_account', v)}
                                className="w-full border rounded px-3 py-2"
                            />
                        </div>
                        <div>
                            <label className="text-xs text-gray-500 block mb-1">Forma de Recebimento *</label>
                            <select value={payment.payment_method}
                                onChange={e => updatePayment('payment_method', e.target.value)}
                                className="w-full border rounded px-3 py-2">
                                <option value="FINANCIAL">FINANCEIRO (TED/PIX)</option>
                                <option value="BOLETO">BOLETO</option>
                                <option value="DEBT_CONFESSION">CONFISSÃO DE DÍVIDA</option>
                                <option value="ASSET_DATION">DAÇÃO (Grãos/Veículo/Imóvel)</option>
                            </select>
                        </div>
                        <div className="col-span-2">
                            <label className="text-xs text-gray-500 block mb-1">Observações</label>
                            <input type="text" value={payment.notes}
                                onChange={e => updatePayment('notes', e.target.value)}
                                placeholder="Referência bancária, NF, etc."
                                className="w-full border rounded px-3 py-2" />
                        </div>
                    </div>
                    <div className="mt-6 flex justify-end">
                        <button
                            disabled={!payment.amount || !payment.date || !payment.payer_document}
                            onClick={() => setCurrentStep(1)}
                            className="px-6 py-2 bg-blue-600 text-white rounded-lg disabled:opacity-50 hover:bg-blue-700">
                            Avançar →
                        </button>
                    </div>
                </div>
            )}

            {/* ═══ STEP 2: Select Contracts ═══ */}
            {currentStep === 1 && (
                <div className="bg-white rounded-lg shadow-sm">
                    <div className="p-4 border-b flex items-center gap-4">
                        <div className="relative flex-1">
                            <FaSearch className="absolute left-3 top-3 text-gray-400" />
                            <input type="text" placeholder="Buscar contrato..."
                                className="w-full pl-10 pr-4 py-2 border rounded-lg"
                                value={searchTerm} onChange={e => setSearchTerm(e.target.value)}
                                onKeyDown={e => e.key === 'Enter' && searchContracts()} />
                        </div>
                        <button onClick={searchContracts} disabled={searchTerm.length < 2}
                            className="px-4 py-2 bg-gray-700 text-white rounded-lg disabled:opacity-50 hover:bg-gray-800">Buscar</button>
                        <div className="text-right text-sm">
                            <div className="font-semibold text-green-700">Valor: R$ {fmt(paymentAmount)}</div>
                        </div>
                    </div>

                    {selectedContracts.length > 0 && (
                        <div className="px-4 py-3 bg-blue-50 border-b">
                            <span className="text-xs font-semibold text-blue-700 uppercase">Selecionados:</span>
                            <div className="flex flex-wrap gap-2 mt-1">
                                {selectedContracts.map(c => (
                                    <span key={c.credit_operation_id} className="inline-flex items-center gap-1 px-2 py-1 bg-white border border-blue-200 rounded text-sm">
                                        {c.operation_number}
                                        <button onClick={() => removeSelectedContract(c.credit_operation_id)} className="text-gray-400 hover:text-red-500 ml-1">&times;</button>
                                    </span>
                                ))}
                            </div>
                        </div>
                    )}

                    {loading ? (
                        <div className="p-8 text-center text-gray-400">Buscando...</div>
                    ) : !searchExecuted ? (
                        <div className="p-8 text-center text-gray-400">Busque o contrato por nome ou número</div>
                    ) : searchResults.length === 0 ? (
                        <div className="p-8 text-center text-gray-400">Nenhum resultado</div>
                    ) : (
                        <div className="divide-y max-h-[50vh] overflow-y-auto">
                            {searchResults.map((contract, idx) => (
                                <div key={contract.credit_operation_id}>
                                    <div className="flex items-center px-4 py-3 hover:bg-gray-50 cursor-pointer" onClick={() => toggleContractExpand(idx)}>
                                        <button className="mr-3" onClick={e => { e.stopPropagation(); toggleContractSelect(idx); }}>
                                            {contract.selected ? <FaCheckSquare className="text-blue-600 text-lg" /> : <FaRegSquare className="text-lg text-gray-400" />}
                                        </button>
                                        <span className="mr-2 text-gray-400">{contract.expanded ? <FaChevronDown /> : <FaChevronRight />}</span>
                                        <div className="flex-1">
                                            <span className="font-medium">{contract.operation_number}</span>
                                            <span className="ml-2 text-xs px-2 py-0.5 bg-gray-100 rounded">{contract.operation_type}</span>
                                            {contract.participants[0] && <span className="ml-2 text-xs text-gray-500">{contract.participants[0].name}</span>}
                                        </div>
                                        <span className="text-sm font-medium text-green-700">
                                            {contract.debtsLoaded ? `R$ ${fmt(contract.total_open)}` : `R$ ${fmt(contract.approved_value)}`}
                                        </span>
                                    </div>
                                    {contract.expanded && contract.debts.length > 0 && (
                                        <div className="bg-gray-50 px-12 py-2 text-xs text-gray-500">
                                            {contract.debts.slice(0, 5).map(d => (
                                                <div key={d.id} className="flex justify-between py-0.5">
                                                    <span>{(d as any).debtor?.name || d.document_title || `Parcela ${d.installment_number}`}</span>
                                                    <span>R$ {fmt(Number(d.amount) - Number(d.paid_amount))}</span>
                                                </div>
                                            ))}
                                            {contract.debts.length > 5 && <div className="text-center mt-1">+{contract.debts.length - 5} títulos...</div>}
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}

                    <div className="p-4 border-t flex items-center justify-between bg-gray-50">
                        <span className="text-sm text-gray-600">{selectedContracts.length} contrato(s)</span>
                        <div className="flex gap-3">
                            <button onClick={() => setCurrentStep(0)} className="px-4 py-2 border rounded-lg hover:bg-gray-100">← Voltar</button>
                            <button disabled={selectedContracts.length === 0 || loading} onClick={goToStep3}
                                className="px-6 py-2 bg-blue-600 text-white rounded-lg disabled:opacity-50 hover:bg-blue-700">
                                {loading ? 'Carregando...' : 'Avançar →'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* ═══ STEP 3: Select Titles (smart) ═══ */}
            {currentStep === 2 && (
                <div className="bg-white rounded-lg shadow-sm">
                    {/* Summary bar */}
                    <div className="p-4 border-b flex items-center justify-between">
                        <div>
                            <span className="text-sm text-gray-600">Valor do recebimento:</span>
                            <span className="ml-2 font-bold text-lg">R$ {fmt(paymentAmount)}</span>
                        </div>
                        <div className="flex items-center gap-4">
                            <div className="text-right">
                                <div className="text-sm text-gray-500">Selecionado: <span className="font-semibold text-green-700">R$ {fmt(totalSelected)}</span></div>
                                <div className={`text-xs ${diff > 0 ? 'text-red-500' : diff < 0 ? 'text-yellow-600' : 'text-green-600'}`}>
                                    {diff === 0 ? '✓ Exato' : diff > 0 ? `↑ R$ ${fmt(diff)} excedente` : `↓ R$ ${fmt(Math.abs(diff))} restante`}
                                </div>
                            </div>
                            <button onClick={() => { const all = selectedContracts.flatMap(c => c.debts).sort((a, b) => (a.due_date || '').localeCompare(b.due_date || '')); autoSelectDebts(all); }}
                                className="flex items-center gap-1 px-3 py-1.5 bg-purple-100 text-purple-700 rounded hover:bg-purple-200 text-sm">
                                <FaMagic /> Auto-selecionar
                            </button>
                        </div>
                    </div>

                    <div className="divide-y max-h-[55vh] overflow-y-auto">
                        {selectedContracts.map(contract => (
                            <div key={contract.credit_operation_id}>
                                <div className="flex items-center px-4 py-2 bg-gray-50 border-b">
                                    <span className="font-semibold text-gray-800">{contract.operation_number}</span>
                                    <span className="ml-3 text-xs text-gray-500">
                                        {contract.debts.filter(d => isDebtSelected(d)).length} / {contract.debts.length} selecionados
                                    </span>
                                </div>
                                <div className="px-4">
                                    <table className="w-full text-sm">
                                        <thead><tr className="text-xs text-gray-400">
                                            <th className="w-8"></th>
                                            <th className="text-left py-1">Sacado</th>
                                            <th className="text-left">Título</th>
                                            <th className="text-right">Saldo</th>
                                            <th className="text-right">Vencimento</th>
                                        </tr></thead>
                                        <tbody>
                                            {contract.debts.map(debt => (
                                                <tr key={debt.id} className={`border-t border-gray-100 cursor-pointer ${isDebtSelected(debt) ? 'bg-blue-50' : 'hover:bg-gray-50'}`}
                                                    onClick={() => toggleDebtSelect(debt)}>
                                                    <td className="py-1.5">
                                                        {isDebtSelected(debt) ? <FaCheckSquare className="text-blue-600" /> : <FaRegSquare className="text-gray-300" />}
                                                    </td>
                                                    <td className="truncate max-w-[200px]">{(debt as any).debtor?.name || debt.document_title || '-'}</td>
                                                    <td className="text-gray-500">{debt.document_title || debt.installment_number || '-'}</td>
                                                    <td className="text-right font-medium">R$ {fmt(Number(debt.amount) - Number(debt.paid_amount))}</td>
                                                    <td className="text-right text-gray-500">{debt.due_date || '-'}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        ))}
                    </div>

                    <div className="p-4 border-t flex items-center justify-between bg-gray-50">
                        <div>
                            <span className="text-sm">{selectedDebts.length} títulos</span>
                            <span className="ml-2 font-bold text-green-700">R$ {fmt(totalSelected)}</span>
                        </div>
                        <div className="flex gap-3">
                            <button onClick={() => setCurrentStep(1)} className="px-4 py-2 border rounded-lg hover:bg-gray-100">← Voltar</button>
                            <button disabled className="px-6 py-2 bg-green-600 text-white rounded-lg opacity-50 cursor-not-allowed"
                                title="Persistência não implementada">
                                Confirmar Baixa (em breve)
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default RegisterPaymentPage;
